import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useParams, Link } from 'react-router-dom'
import { api, Company, Employee } from '../lib/api'
import { ArrowLeft, Users, Wallet, CreditCard, Plus, Mail } from 'lucide-react'
import { useState } from 'react'
import clsx from 'clsx'

const formatCurrency = (pence: number) => 
  new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(pence / 100)

const statusColors: Record<string, string> = {
  active: 'bg-green-100 text-green-700',
  invited: 'bg-blue-100 text-blue-700',
  pending_kyc: 'bg-amber-100 text-amber-700',
  suspended: 'bg-red-100 text-red-700'
}

export default function CompanyDetail() {
  const { id } = useParams<{ id: string }>()
  const queryClient = useQueryClient()
  const [showInvite, setShowInvite] = useState(false)
  const [inviteEmail, setInviteEmail] = useState('')
  const [inviteName, setInviteName] = useState('')

  const { data: company, isLoading } = useQuery({
    queryKey: ['company', id],
    queryFn: () => api.get<Company>(`/companies/${id}`)
  })

  const { data: employees } = useQuery({
    queryKey: ['company-employees', id],
    queryFn: () => api.get<Employee[]>(`/companies/${id}/employees`)
  })

  const inviteMutation = useMutation({
    mutationFn: (data: { email: string; name: string }) => 
      api.post(`/companies/${id}/employees/invite`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['company-employees', id] })
      setShowInvite(false)
      setInviteEmail('')
      setInviteName('')
    }
  })

  if (isLoading) return <div className="animate-pulse">Loading...</div>
  if (!company) return <div>Company not found</div>

  return (
    <div className="space-y-6">
      <Link to="/companies" className="flex items-center gap-2 text-gray-600 hover:text-gray-900">
        <ArrowLeft size={20} />
        Back to Companies
      </Link>

      {/* Company Header */}
      <div className="bg-white rounded-xl p-6 shadow-sm">
        <h1 className="text-2xl font-bold text-gray-900">{company.name}</h1>
        <p className="text-gray-500">{company.billing_email}</p>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
          <div className="bg-gray-50 rounded-lg p-4">
            <Users className="text-salad-600 mb-2" size={24} />
            <div className="text-2xl font-bold">{company.employee_count}</div>
            <div className="text-sm text-gray-500">Employees</div>
          </div>
          <div className="bg-gray-50 rounded-lg p-4">
            <CreditCard className="text-salad-600 mb-2" size={24} />
            <div className="text-2xl font-bold">{company.active_card_count}</div>
            <div className="text-sm text-gray-500">Active Cards</div>
          </div>
          <div className="bg-gray-50 rounded-lg p-4">
            <Wallet className="text-salad-600 mb-2" size={24} />
            <div className="text-2xl font-bold">{formatCurrency(company.total_funded)}</div>
            <div className="text-sm text-gray-500">Total Funded</div>
          </div>
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="text-2xl font-bold">{formatCurrency(company.default_monthly_allowance)}</div>
            <div className="text-sm text-gray-500">Monthly Allowance</div>
          </div>
        </div>
      </div>

      {/* Employees */}
      <div className="bg-white rounded-xl p-6 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">Employees</h2>
          <button
            onClick={() => setShowInvite(true)}
            className="flex items-center gap-2 bg-salad-600 text-white px-4 py-2 rounded-lg hover:bg-salad-700"
          >
            <Plus size={18} />
            Invite Employee
          </button>
        </div>

        {showInvite && (
          <div className="bg-gray-50 rounded-lg p-4 mb-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <input
                type="text"
                placeholder="Name"
                value={inviteName}
                onChange={(e) => setInviteName(e.target.value)}
                className="px-4 py-2 border rounded-lg"
              />
              <input
                type="email"
                placeholder="Email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                className="px-4 py-2 border rounded-lg"
              />
            </div>
            <div className="flex gap-2 mt-4">
              <button
                onClick={() => inviteMutation.mutate({ email: inviteEmail, name: inviteName })}
                disabled={!inviteEmail || !inviteName}
                className="bg-salad-600 text-white px-4 py-2 rounded-lg disabled:opacity-50"
              >
                Send Invite
              </button>
              <button onClick={() => setShowInvite(false)} className="px-4 py-2 text-gray-600">
                Cancel
              </button>
            </div>
          </div>
        )}

        <div className="space-y-2">
          {employees?.map(emp => (
            <div key={emp.id} className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
              <div className="w-10 h-10 bg-salad-200 rounded-full flex items-center justify-center font-medium text-salad-700">
                {emp.name.charAt(0)}
              </div>
              <div className="flex-1">
                <div className="font-medium">{emp.name}</div>
                <div className="text-sm text-gray-500">{emp.email}</div>
              </div>
              <span className={clsx('px-2 py-1 rounded-full text-xs font-medium', statusColors[emp.status])}>
                {emp.status}
              </span>
              <div className="text-right">
                <div className="font-medium">{formatCurrency(emp.current_balance)}</div>
                <div className="text-xs text-gray-500">balance</div>
              </div>
            </div>
          ))}
          {employees?.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              <Mail className="mx-auto mb-2" size={32} />
              No employees yet. Invite your first employee!
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
